import { useEffect, useState } from "react";
import { useServiceOdo } from "../../hooks/service/useServiceOdo";
import CommonSidebar from "../../components/dashboard/CommonSidebar";
import { DataTableColumn } from "../../types/dataTable";
import DataTable from "../../components/common/DataTable";
import {
  OdoItem,
  DirectReplyRequest,
  DirectReplyUpdateRequest,
  DirectReplyDeleteRequest,
} from "../../types/odo";
import { useDebounce } from "../../hooks/common/useDebounce";
import OdoModal from "../../components/service/OdoModal";
import OdoSearchBar from "../../components/management/OdoSearchBar";
import toast from "react-hot-toast";

export default function Odo() {
  const {
    odoList,
    odoDetail,
    totalCount,
    currentPage,
    isLoading,
    isReplyLoading,
    error,
    replyError,
    detailError,
    totalPages,
    isEmpty,
    fetchOdoList,
    fetchOdoSearch,
    fetchOdoDetail,
    DirectReplyCreate,
    DirectReplyUpdate,
    DirectReplyDelete,
    setCurrentPage,
  } = useServiceOdo();

  const [searchTerm, setSearchTerm] = useState("");
  const [processStatus, setProcessStatus] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // 필터 옵션 정의
  const processStatusOptions = [
    { value: "", label: "전체 처리상태" },
    { value: "답변대기", label: "답변대기" },
    { value: "추가 답변대기", label: "추가 답변대기" },
    { value: "답변완료", label: "답변완료" },
  ];

  const serviceTypeOptions = [
    { value: "", label: "전체 서비스" },
    { value: "home", label: "Home" },
    { value: "basic_studio", label: "Basic Studio" },
    { value: "aistudio", label: "AI Studio" },
    { value: "expert_update", label: "Expert Update" },
  ];

  // 필터나 검색어 변경 시 페이지 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, processStatus, serviceType, setCurrentPage]);

  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      fetchOdoSearch(debouncedSearchTerm);
    } else {
      fetchOdoList(currentPage, processStatus, serviceType);
    }
  }, [
    currentPage,
    debouncedSearchTerm,
    processStatus,
    serviceType,
    fetchOdoList,
    fetchOdoSearch,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
      if (searchTerm.trim()) {
        fetchOdoSearch(searchTerm);
      } else {
        fetchOdoList(1, processStatus, serviceType);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // 필터 변경
  const handleFilterChange = (
    type: "processStatus" | "serviceType",
    value: string
  ) => {
    if (type === "processStatus") {
      setProcessStatus(value);
    } else {
      setServiceType(value);
    }
  };

  // 필터 초기화
  const handleClearAllFilters = () => {
    setSearchTerm("");
    setProcessStatus("");
    setServiceType("");
  };

  const handleRowClick = async (row: OdoItem) => {
    try {
      await fetchOdoDetail(row.id);
      setIsDetailOpen(true);
    } catch (error) {
      const errorMsg = detailError || "상세 정보 조회 중 오류가 발생했습니다.";
      toast.error(errorMsg);
    }
  };

  const handleModalClose = () => {
    setIsDetailOpen(false);
  };

  // 답변 전송
  const handleReplySubmit = async (replyData: DirectReplyRequest) => {
    const success = await DirectReplyCreate(replyData);
    if (success) {
      await fetchOdoDetail(odoDetail?.id || "");
      toast.success("답변이 전송되었습니다.");
      return true;
    } else {
      const errorMsg = replyError || "답변 전송 중 오류가 발생했습니다.";
      toast.error(errorMsg);
      return false;
    }
  };

  // 답변 수정
  const handleReplyUpdate = async (replyData: DirectReplyUpdateRequest) => {
    const success = await DirectReplyUpdate(replyData);
    if (success) {
      await fetchOdoDetail(odoDetail?.id || "");
      toast.success("답변이 수정되었습니다.");
      return true;
    } else {
      const errorMsg = replyError || "답변 수정 중 오류가 발생했습니다.";
      toast.error(errorMsg);
      return false;
    }
  };

  // 답변 삭제
  const handleReplyDelete = async (replyData: DirectReplyDeleteRequest) => {
    const success = await DirectReplyDelete(replyData);
    if (success) {
      await fetchOdoDetail(odoDetail?.id || "");
      toast.success("답변이 삭제되었습니다.");
      return true;
    } else {
      const errorMsg = replyError || "답변 삭제 중 오류가 발생했습니다.";
      toast.error(errorMsg);
      return false;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const columns: DataTableColumn<OdoItem>[] = [
    {
      key: "title",
      header: "제목",
      render: (value) => (
        <div className="max-w-xs">
          <h3 className="text-white font-medium truncate">{value}</h3>
        </div>
      ),
    },
    {
      key: "service_type",
      header: "서비스 타입",
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
          {serviceTypeOptions.find((opt) => opt.value === value)?.label}
        </span>
      ),
    },
    {
      key: "nickname",
      header: "닉네임",
      render: (value) => (
        <div className="max-w-xs">
          <h3 className="text-white font-medium truncate">{value}</h3>
        </div>
      ),
    },
    {
      key: "process_status",
      header: "처리 상태",
      render: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value === "답변대기"
              ? "bg-yellow-500/20 text-yellow-400"
              : value === "추가 답변대기"
              ? "bg-orange-500/20 text-orange-400"
              : "bg-green-500/20 text-green-400"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "생성일",
      render: (value) => (
        <div className="max-w-xs">
          <h3 className="text-white font-medium truncate">
            {formatDate(value)}
          </h3>
        </div>
      ),
    },
  ];

  const paginationInfo = {
    currentPage,
    totalPages,
    totalCount,
    startIndex: (currentPage - 1) * 10,
    endIndex: Math.min(currentPage * 10, totalCount),
    onPageChange: (page: number) => {
      setCurrentPage(page);
      if (debouncedSearchTerm.trim()) {
        fetchOdoSearch(debouncedSearchTerm);
      } else {
        fetchOdoList(page, processStatus, serviceType);
      }
    },
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">문의 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <OdoSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            processStatus={processStatus}
            serviceType={serviceType}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            processStatusOptions={processStatusOptions}
            serviceTypeOptions={serviceTypeOptions}
            totalCount={odoList.length}
            debouncedSearchTerm={debouncedSearchTerm}
          />
          <div
            className={`bg-slate-900 rounded-xl border border-slate-800 overflow-hidden p-6`}
          >
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
              <span className="ml-3 text-slate-400">데이터를 불러오는 중...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">문의 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <OdoSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            processStatus={processStatus}
            serviceType={serviceType}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            processStatusOptions={processStatusOptions}
            serviceTypeOptions={serviceTypeOptions}
            totalCount={odoList.length}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          {/* 유저 리스트 테이블 */}
          <DataTable<OdoItem>
            columns={columns}
            data={odoList}
            pagination={paginationInfo}
            loading={false}
            error={null}
            emptyMessage="작성된 문의가 없습니다."
          />
        </div>
      </div>
    );
  }

  if (error || detailError) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">문의 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <OdoSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            processStatus={processStatus}
            serviceType={serviceType}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            processStatusOptions={processStatusOptions}
            serviceTypeOptions={serviceTypeOptions}
            totalCount={totalCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          <DataTable<OdoItem>
            columns={columns}
            data={[]}
            pagination={undefined}
            loading={false}
            error={error || detailError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 lg:ml-72">
      <div className="bg-slate-900 border-b border-slate-800 p-6">
        <h1 className="text-2xl font-bold text-white">문의 관리</h1>
      </div>
      <CommonSidebar />
      <div className="p-6">
        <OdoSearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          onClearSearch={handleClearSearch}
          processStatus={processStatus}
          serviceType={serviceType}
          onFilterChange={handleFilterChange}
          onClearAllFilters={handleClearAllFilters}
          processStatusOptions={processStatusOptions}
          serviceTypeOptions={serviceTypeOptions}
          totalCount={totalCount}
          debouncedSearchTerm={debouncedSearchTerm}
        />

        <DataTable<OdoItem>
          columns={columns}
          data={odoList}
          pagination={paginationInfo}
          loading={isLoading}
          error={error}
          onRowClick={handleRowClick}
        />

        <OdoModal
          isOpen={isDetailOpen}
          onClose={handleModalClose}
          odoDetail={odoDetail}
          onReplySubmit={handleReplySubmit}
          onReplyUpdate={handleReplyUpdate}
          onReplyDelete={handleReplyDelete}
          isLoading={isReplyLoading}
        />
      </div>
    </div>
  );
}
